import type { ChannelLineupView } from "./channel-lineup";
import type {
  LineupCategory,
  LineupChannel,
  LineupColumn,
  LineupPack,
  LineupSubscriptionRow,
  LocalsStatus
} from "./channel-lineup.types";
import type { LineupSearchSource } from "./lineup-search.shared";
import type { LineupPlatform } from "./signature-grid.shared";
import { SORT_VALUE_AZ } from "./sort-radios.shared";
import { CHANNEL_LINEUP_VIEWS } from "./view-toggle.shared";

/** The synchronous half of the data seam, and it has to stay synchronous. */

/** Everything the organism needs that comes from the model. It emits `channels`, not `sections`. */
export interface LineupData {
  platform: LineupPlatform;
  /** Authored signature codes, in authored order (§2). */
  columns: LineupColumn[];
  /** Every channel the Signature grid has to bucket, MyFree included. */
  channels: LineupChannel[];
  /** Authored, in authored order; `[]` when none (the grid falls back to A-Z). */
  categories: LineupCategory[];
  /** Authored non-signature codes, in authored order — `GenrePackListProps.packs`. */
  packs: LineupPack[];
  /** `MyFreeChannelsProps.channels`. */
  myFreeChannels: LineupChannel[];
  /** `LocalsRsnProps.localChannels` — this market's locals. */
  localChannels: LineupChannel[];
  /** `LocalsRsnProps.rsns` — the beam in-market RSNs, passed through from the
   *  same `LineupMarket` the rest of this result was built from (G1). */
  rsns: LineupChannel[];
  /** `LocalsRsnProps.localChannelsStatus` — the locals lookup's own outcome,
   *  echoed from the market. */
  localChannelsStatus: LocalsStatus;
  /**
   * `LocalsRsnProps.rsnsStatus` — the beam call's own outcome. A second field, never a second
   * reading of the first: §5's "locals keep rendering while the RSN rows show the error" is not
   * expressible with one flag.
   */
  rsnsStatus: LocalsStatus;
  /** GS1's "Included subscriptions" rows. */
  subscriptionRows: LineupSubscriptionRow[];
  /**
   * The search and the grid are one resolve, and this is the mechanism. The very arrays above, from
   * the same call — so a composer cannot hand the suggestion list a different market's data than
   * the grid.
   */
  searchSource: LineupSearchSource;
  /** The DMA this resolve is scoped to, echoed from the market. */
  dma?: string;
  /** GS1's content-managed MyFree wording, passed through. */
  myFreeLabel?: string;
  /**
   * Authored codes the feed does not carry. They are dropped, never rendered as an empty column
   * (§2) — surfaced here so a caller can assert on it and so PG6 can tell "nothing authored" from
   * "everything authored was dropped".
   */
  droppedCodes: string[];
}

/**
 * The blok fields this seam reads — DATA-CONTRACT §Layer 3. The authored ones are typed loosely on
 * purpose: they arrive from `commonSbFixer`, which collapses a one-element array to the element and
 * drops falsy values, so nothing here may assume an array survived as one.
 */
export interface LineupBlok {
  /** The authored package list, in authored order; a code missing from the
   *  feed is dropped by the resolver and never renders as an empty column. */
  packages?: unknown;
  /** GS3/GS5 — the authored category buckets, matched against the feed's
   *  `categories[]`. */
  categories?: unknown;
  /** Which feed the codes are looked up in. `"ott"` | `"satellite"`; anything else reads as `"ott"`. */
  platform?: string;
  /**
   * VT2's view. Authored it is step 4's default; by the time the canvas reads it,
   * `dynamic-page-components-adapter.ts` has usually replaced it with `resolveInitialView`'s answer
   * for this request.
   */
  view?: string;
  /** GH4's authored default sort. See `sort` on `ResolvedLineup`. */
  sort?: string;
  /**
   * VT1/VT2 — the view toggle's options, in authored order. Each `value` is the view it selects
   * (`signature` | `genre`, the two `VIEWS` below), and each `label` is the wording VT2 step 1
   * matches "genre" against.
   */
  view_options?: unknown;
  /**
   * GH4 — the sort control's options, in authored order. Nothing usable falls back to A-Z /
   * Category (`prepareSortOptions`). Needed for the Channel Number sort (`SORT_VALUE_NUMBER`),
   * which is not in that fallback pair.
   */
  sort_options?: unknown;
  /**
   * VT2 steps 2-4 — the content-managed "genre trigger" pages, as referrer substrings. Read by the
   * adapter, not by this seam: the referrer is request state and never reaches the canvas.
   */
  genre_trigger_pages?: unknown;
  /**
   * Resolver-fed, not authored — the finished read, attached by `attachLineupData`
   * (`lib/channel-lineup/lineup-page-data.ts`) before the canvas runs. CamelCase for exactly that
   * reason; an author cannot publish this name, and its absence is PG6.
   */
  lineupData?: LineupData;
}

/**
 * What the composer needs to draw the whole lineup — the resolver's output plus the two pieces of
 * root state nothing resolves today.
 */
export interface ResolvedLineup extends LineupData {
  /** PG3/VT3 — which view the root draws. */
  view: ChannelLineupView;
  /** GH4 — the sort `buildSignatureSections` buckets by. */
  sort: string;
}

/** This still reads only the blok, and that is now correct. */
export function resolveLineupForBlok(blok: LineupBlok = {}): ResolvedLineup | null {
  const authoredView = (blok.view ?? "").trim().toLowerCase();
  // `authoredView` has already passed the adapter's own closed- vocabulary guard on every path that
  // writes `?view=` onto the blok — so landing here means either nothing was authored (blank) or
  // the default the adapter left untouched is itself malformed.
  const inVocabulary = Object.prototype.hasOwnProperty.call(CHANNEL_LINEUP_VIEWS, authoredView);
  if (authoredView !== "" && !inVocabulary) {
    console.warn(
      `[ChannelLineup] blok.view "${blok.view}" is not in the closed vocabulary ` +
        `(${Object.keys(CHANNEL_LINEUP_VIEWS).join(", ")}); falling back to "signature".`
    );
  }
  const view: ChannelLineupView = inVocabulary ? (authoredView as ChannelLineupView) : "signature";
  const sort = (blok.sort ?? "").trim() || SORT_VALUE_AZ;

  const lineup = blok.lineupData;
  /* PG6, first clause — nothing attached a resolved model to this blok. There is no fallback data
     source (DATA-CONTRACT §5), so this is the error state rather than a fixture. */
  if (!lineup) {
    return null;
  }

  /* PG6, second clause — a cold or empty `channelsService:model:<platform>` key (§5), which the
     composer spells `null`. `&&` rather than `||`, on purpose: this is "the model carried nothing",
     the only state PG6 names. */
  if (lineup.columns.length === 0 && lineup.packs.length === 0) {
    return null;
  }

  return {
    ...lineup,
    sort,
    view
  };
}
